"use client";

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { hasClinicRole } from "@/context/AuthContext";
import { useImpersonation } from "@/context/ImpersonationContext";
import { api } from "@/lib/api-client";
import { scopedStorageKey } from "@/lib/impersonation-storage";
import { MenuDataResponse } from "@/types/menu";

interface MenuDataContextType {
  menuData: MenuDataResponse | null;
  loading: boolean;
  refreshMenuData: () => Promise<void>;
}

const MenuDataContext = createContext<MenuDataContextType | undefined>(undefined);

// Menu data (clinic config, subscription, pending tasks) rarely changes within
// a session. Caching it per tab means only the very first screen after
// sign-in pays for the /menu/data round trip — a later screen, including a
// hard refresh in the same tab, can render the sidebar/nav instantly from
// cache while loadMenuData still refetches in the background to stay current.
// Guards against a stale cache entry written by a previous, differently-shaped
// deploy (continuous deployment + a long-lived tab makes this plausible): the
// sidebar/hamburger badge dereference `menuData.client`/`menuData.subscription`
// without optional chaining, trusting that a non-null `menuData` matches
// today's shape.
function isMenuDataResponse(value: unknown): value is MenuDataResponse {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as MenuDataResponse).pending_tasks_count === "number"
  );
}

function readCachedMenuData(key: string): MenuDataResponse | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isMenuDataResponse(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedMenuData(key: string, data: MenuDataResponse) {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch {
    // Private browsing / quota errors — the cache is a nice-to-have, not required.
  }
}

export function MenuDataProvider({ children }: { children: ReactNode }) {
  const [menuData, setMenuData] = useState<MenuDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const { ready: impersonationReady } = useImpersonation();

  const loadMenuData = useCallback(async (isInitialLoad = false) => {
    if (!user) {
      setMenuData(null);
      // Only report "loaded" once auth has definitively resolved with no user.
      // While auth is still hydrating (authLoading), keep loading=true so
      // consumers — notably the multi-location route guard — don't mistake
      // "auth not ready yet" for "no client / feature off" and redirect early.
      if (!authLoading) {
        setLoading(false);
      }
      return;
    }

    // Partner-only users have no clinic: /menu/data would 403 and log noise.
    if (!hasClinicRole(user)) {
      setMenuData(null);
      setLoading(false);
      return;
    }

    // For admin users, wait until ImpersonationContext has settled on a
    // client (stored or auto-selected) — otherwise this fires without
    // X-Client-Id and 403s. The effect re-runs once impersonationReady flips.
    if (!impersonationReady) {
      return;
    }

    const cacheKey = scopedStorageKey(`menuData:${user.userId}`);
    let usedCache = false;

    if (isInitialLoad) {
      const cached = readCachedMenuData(cacheKey);
      if (cached) {
        setMenuData(cached);
        setLoading(false);
        usedCache = true;
      } else {
        // Only set loading state during initial load to prevent UI glitches on refresh
        setLoading(true);
      }
    }

    try {
      const data = await api.getMenuData();
      setMenuData(data);
      writeCachedMenuData(cacheKey, data);
    } catch (error) {
      console.error("Failed to load menu data:", error);
      // A cached copy is still good data — don't blank the sidebar/nav over a
      // transient refetch failure.
      if (!usedCache) {
        setMenuData(null);
      }
    } finally {
      if (isInitialLoad && !usedCache) {
        setLoading(false);
      }
    }
  }, [user, authLoading, impersonationReady]);

  useEffect(() => {
    loadMenuData(true); // Initial load
  }, [loadMenuData]);

  return (
    <MenuDataContext.Provider
      value={{
        menuData,
        loading,
        refreshMenuData: loadMenuData,
      }}
    >
      {children}
    </MenuDataContext.Provider>
  );
}

export function useMenuData() {
  const context = useContext(MenuDataContext);
  if (context === undefined) {
    throw new Error("useMenuData must be used within a MenuDataProvider");
  }
  return context;
}
